import { supabase } from '../lib/supabase';

const toCamel = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamel);
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, l) => l?.toUpperCase());
    acc[camelKey] = toCamel(obj?.[key]);
    return acc;
  }, {});
};

export const inspectionAiService = {
  /**
   * Save AI recommendations to DB linked to work order
   */
  async save(workOrderId, companyId, recommendations, partsList, rawInput) {
    try {
      const { data, error } = await supabase
        ?.from('inspection_ai_recommendations')
        ?.upsert({
          work_order_id: workOrderId,
          company_id: companyId,
          recommendations,
          parts_list: partsList || [],
          raw_input: rawInput || {},
          is_edited: false,
          updated_at: new Date()?.toISOString()
        }, { onConflict: 'work_order_id' })
        ?.select()
        ?.single();
      if (error) throw error;
      return toCamel(data);
    } catch (err) {
      console.error('[inspectionAiService] save error:', err);
      throw err;
    }
  },

  /**
   * Update (mark as edited) and save edited recommendations
   */
  async update(id, recommendations, partsList) {
    try {
      const { data, error } = await supabase
        ?.from('inspection_ai_recommendations')
        ?.update({
          recommendations,
          parts_list: partsList || [],
          is_edited: true,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', id)
        ?.select()
        ?.single();
      if (error) throw error;
      return toCamel(data);
    } catch (err) {
      console.error('[inspectionAiService] update error:', err);
      throw err;
    }
  },

  /**
   * Get existing AI recommendations for a work order
   */
  async getByWorkOrder(workOrderId) {
    try {
      const { data, error } = await supabase
        ?.from('inspection_ai_recommendations')
        ?.select('*')
        ?.eq('work_order_id', workOrderId)
        ?.maybeSingle();
      if (error) return null;
      return toCamel(data);
    } catch {
      return null;
    }
  },

  /**
   * Build the AI prompt from inspection data
   */
  buildPrompt(vehicleInfo, inspectionData) {
    const vehicleStr = vehicleInfo
      ? `Vehículo: ${vehicleInfo?.make || ''} ${vehicleInfo?.model || ''} ${vehicleInfo?.year || ''}, Motor: ${vehicleInfo?.motor || 'N/D'}, Combustible: ${vehicleInfo?.combustible || 'N/D'}, Kilometraje: ${vehicleInfo?.kilometraje ? `${vehicleInfo?.kilometraje} km` : 'N/D'}`
      : 'Vehículo no especificado';

    const categoriesStr = Object.entries(inspectionData || {})?.map(([cat, data]) => {
        const catNames = { brakes: 'Frenos', suspension: 'Suspensión', tuning: 'Motor/Afinación', others: 'Otros Sistemas' };
        const name = catNames?.[cat] || cat;
        return `${name}:\n  Estado: ${data?.status || 'N/D'}\n  Hallazgos: ${data?.findings || 'Sin hallazgos'}\n  Recomendaciones previas: ${data?.recommendations || 'Ninguna'}`;
      })?.join('\n\n');

    return `Eres un mecánico experto. Analiza la siguiente inspección técnica vehicular y genera:
1. Recomendaciones técnicas claras y priorizadas (urgente, próximo servicio, preventivo)
2. Lista de refacciones sugeridas con nombre, cantidad estimada y nota técnica

Responde SOLO en formato JSON con esta estructura exacta:
{
  "recommendations": "texto con recomendaciones técnicas detalladas",
  "parts": [
    {"name": "nombre de la pieza", "qty": 1, "note": "nota técnica"}
  ]
}

${vehicleStr}

Hallazgos de inspección:
${categoriesStr}`;
  }
};
